import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useStore } from '../contexts/StoreContext';
import { getPrimaryImage } from '../utils/imageUtils';

interface CartProps {
  isOpen: boolean;
  onClose: () => void;
}

const Cart: React.FC<CartProps> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const { cart, removeFromCart, updateCartItemQuantity, clearCart, getTotalPrice } = useStore();

  const handleQuantityChange = useCallback((itemId: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
    } else {
      updateCartItemQuantity(itemId, newQuantity);
    }
  }, [removeFromCart, updateCartItemQuantity]);

  const handleCheckout = useCallback(() => {
    onClose(); // Close the cart sidebar
    // Navigate to checkout page will be handled by the Link component
  }, [onClose]);

  const handleClearCart = useCallback(() => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      clearCart();
    }
  }, [clearCart]);

  // Memoize cart items to prevent unnecessary re-renders
  const cartItems = useMemo(() => cart, [cart]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Cart Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white transform transition-transform duration-300 ease-in-out z-50 shadow-2xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-primary">{t.shopping.cart}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close cart"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {cartItems.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" clipRule="evenodd" d="M3.25 4C3.25 3.58579 3.58579 3.25 4 3.25H5.14208C6.00583 3.25 6.74039 3.88019 6.87173 4.7339L7.11776 6.33306C7.28591 6.2792 7.46564 6.25 7.65301 6.25H17.347C18.412 6.25 19.23 7.19323 19.0794 8.24749L18.6104 11.5303C18.5185 12.1738 18.2666 12.7597 17.8985 13.25H18C19.2426 13.25 20.25 14.2574 20.25 15.5C20.25 16.5376 19.5476 17.4112 18.5924 17.6712C18.6941 17.9277 18.75 18.2073 18.75 18.5C18.75 19.7426 17.7426 20.75 16.5 20.75C15.2574 20.75 14.25 19.7426 14.25 18.5C14.25 18.237 14.2951 17.9846 14.378 17.75H10.622C10.7049 17.9846 10.75 18.237 10.75 18.5C10.75 19.7426 9.74264 20.75 8.5 20.75C7.25736 20.75 6.25 19.7426 6.25 18.5C6.25 17.2574 7.25736 16.25 8.5 16.25H18C18.4142 16.25 18.75 15.9142 18.75 15.5C18.75 15.0858 18.4142 14.75 18 14.75H8.3673C7.49641 14.75 6.75805 14.1096 6.63488 13.2475L6.25811 10.6101L5.38917 4.96199C5.37041 4.84003 5.26547 4.75 5.14208 4.75H4C3.58579 4.75 3.25 4.41421 3.25 4ZM7.74266 10.3953L8.11981 13.0354C8.1374 13.1585 8.24288 13.25 8.3673 13.25H14.8981C16.0178 13.25 16.9671 12.4267 17.1255 11.3182L17.5945 8.03535C17.616 7.88475 17.4991 7.75 17.347 7.75H7.65301C7.50087 7.75 7.38401 7.88475 7.40552 8.03536L7.74231 10.3929C7.74243 10.3937 7.74254 10.3945 7.74266 10.3953ZM16.5 17.75C16.0858 17.75 15.75 18.0858 15.75 18.5C15.75 18.9142 16.0858 19.25 16.5 19.25C16.9142 19.25 17.25 18.9142 17.25 18.5C17.25 18.0858 16.9142 17.75 16.5 17.75ZM8.5 17.75C8.08579 17.75 7.75 18.0858 7.75 18.5C7.75 18.9142 8.08579 19.25 8.5 19.25C8.91421 19.25 9.25 18.9142 9.25 18.5C9.25 18.0858 8.91421 17.75 8.5 17.75Z"/>
                </svg>
                <p className="text-gray-500 text-lg">{t.shopping.cartEmpty}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((cartItem) => (
                  <div key={cartItem.product.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                     {/* Product Image */}
                     <Link
                       to={`/shopping/item/${cartItem.product.id}`}
                       onClick={onClose}
                       className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden hover:bg-gray-300 transition-colors cursor-pointer"
                     >
                       {getPrimaryImage(cartItem.product) ? (
                         <img
                           src={getPrimaryImage(cartItem.product)!}
                           alt={cartItem.product.name || cartItem.product.name_en || 'Product'}
                           className="w-full h-full object-cover"
                           onError={(e) => {
                             const target = e.target as HTMLImageElement;
                             target.style.display = 'none';
                             target.nextElementSibling?.classList.remove('hidden');
                           }}
                         />
                       ) : null}
                       <svg className={`w-8 h-8 text-gray-400 ${getPrimaryImage(cartItem.product) ? 'hidden' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                         <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                       </svg>
                     </Link>

                     {/* Product Info */}
                     <div className="flex-1">
                       <Link
                         to={`/shopping/item/${cartItem.product.id}`}
                         onClick={onClose}
                         className="font-semibold text-primary hover:text-secondary transition-colors cursor-pointer"
                       >
                         {cartItem.product.name || cartItem.product.name_en || 'Product'}
                       </Link>
                       <p className="text-sm text-gray-600">€{cartItem.product.price.toFixed(2)} each</p>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => handleQuantityChange(cartItem.product.id, cartItem.quantity - 1)}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="w-8 text-center font-semibold">{cartItem.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(cartItem.product.id, cartItem.quantity + 1)}
                          className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeFromCart(cartItem.product.id)}
                      className="text-red-500 hover:text-red-700 transition-colors p-2"
                      aria-label="Remove item"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 p-6">
              {/* Total */}
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold text-primary">{t.shopping.cartTotal}:</span>
                <span className="text-2xl font-bold text-secondary">€{getTotalPrice.toFixed(2)}</span>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Link
                  to="/checkout"
                  onClick={handleCheckout}
                  className="block w-full bg-secondary text-white py-3 px-4 rounded-lg font-semibold hover:bg-secondary/80 transition-colors text-center"
                >
                  {t.shopping.proceedToCheckout}
                </Link>
                
                <button
                  onClick={handleClearCart}
                  className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                  aria-label="Clear all items from cart"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;
